/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useEffect } from 'react';
import StatusCard from '../media_container_inventory/partials/statistics';
import { ContainerInventoryService } from "../../../../service/MediaContainerServices";
import MediaPrepHeaderSection from '../../../common/commonComponents/mediaPrepheaderSection';
import MediaPrepPageHeader from '../../../common/commonComponents/mediaPrepPageHeader';
import { useTranslation } from 'react-i18next';
import ContainerInventoryTable from "../../../common/commonComponents/containerInventoryTable";
import { getColumns } from "./partials/column";
import FilterBar from '../media_container_inventory/partials/containerFilter';
import Pagination from '../../../../widgets/pagination';
import { DEFAULT_PAGE_SIZE } from '../../../../constants/constants';
import ContainerDetails from './views/containerDetails';
import CustomToast from '../../../../widgets/toaster';
import LogMediaPowderModal from "../media_powder_inventory/partials/logMediaModal";
import ContainerLotCreation from "./views/containerLotCreation";
import { useNavigate } from "react-router-dom";
import { userService } from '../../../../service/userService';


const ContainerInventory: React.FC = () => {
  const { t } = useTranslation();
  const [containerItems, setContainerItems] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');  
  const [totalElements, setTotalElements] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [lotFilter, setLotFilter] = useState('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [plateTypes, setPlateTypes] = useState([]);
  const [batchCodes, setBatchCodes] = useState<string[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string>("");
  const [view, setView] = useState<"list" | "lot">("list");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");  
  const [showLogModal, setShowLogModal] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [sort, setSort] = useState("id");
    const [order, setOrder] = useState("desc");
    const handleSort = (key: string, value: boolean) => {
      setSort(key);
      setOrder(value ? "asc" : "desc");
      setCurrentPage(1);
    };


  const [permission, setPermission] = useState<any>([]);
    const getPermissionByRole = async (role: number) => {
      try {
        const response = await userService.getPermissionByRole(role);
  
        if (response.status && response.statusCode === 200) {
          setPermission(response.data.permissions);
        }
      } catch (error) {
        console.error(error);
      }
    };
  
    useEffect(() => {
      const storedData = localStorage.getItem("userDetails");
  
      if (storedData) {
        const storedObject = JSON.parse(storedData);
        getPermissionByRole(storedObject[0].role_master_id);
      }
    }, []);


  const [stats, setStats] = useState({
    available: 0,
    sterilization: 0,
    inUse: 0,
    blocked: 0,
    });


    // api call for statistics listing for container inventory
  const getContainerInventoryStats = async () => {
    try {
        const response = await ContainerInventoryService.getContainerInventoryStats();

        if (response.status && response.statusCode === 200) {
        const data = response.data;

        setStats({
            available: data.by_status.EMPTY_AVAILABLE || 0,
            sterilization: data.by_status.in_sterilization || 0,
            inUse: data.by_status.ready_to_use || 0,
            blocked: data.by_status.discarded || 0,
        });
        }
    } catch (error) {
        console.error("Container stats API error:", error);
    }
  };



  // api call for container plate type listing for container inventory
  const getContainerPlateTypeList = async () => {
    try {
      const response = await ContainerInventoryService.listContainerPlateTypes();

      if (response.status && response.statusCode === 200) {
        const formatted = response.data.map((item: any) => ({
          id: item.id,
          name: item.name
        }));

        setPlateTypes(formatted);
      }
    } catch (error) {
      console.error("Container plate type API error:", error);
    }
  };



  //api call for container data listing
  const getContainerList = async () => {
    setLoading(true);
    try {
      const response = await ContainerInventoryService.getContainerList(currentPage, pageSize, sort, order, searchTerm, lotFilter, mediaTypeFilter, fromDate, toDate
      );

      if (response.status && response.statusCode === 200) {
        const mappedData = response.data.map((element: any, index: number) => {
          const size = element.container_info?.split(" - ")[1] || "-";
          const containerIcons: Record<string, string> = {
            "Petri Plate": "radio_button_unchecked",
            "Contact Plate (RODAC)": "crop_square",
            "Glass Bottle": "science",
            "Plastic Bottle": "local_drink",
            "Test Tube": "reorder",
            "Conical Flask": "experiment",
            "Sampling Bag": "deployed_code",
            "Blood Culture Bottle": "vaccines",
            "Beaker": "lab_profile",
            "Measuring Cylinder": "straighten",
          };
          return {
            id: element.id,
            slno: (currentPage - 1) * pageSize + index + 1,
            batchCode: element.batch_no,
            containerName: element.container_type_name,
            size,
            manufacturer: element.manufacturer || "-",
            quantity: element.total_quantity,
            typeColor: element.container_type === "bg-orange-50 text-orange-400", 
            icon: containerIcons[element.container_type_name] || "inventory_2", 
            iconBg: element.lifecycle_status === "BLOCKED / DAMAGED" ? "bg-red-50 text-red-400" : "bg-orange-50 text-orange-400", }
          }
        );

        setContainerItems(mappedData);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
      } else {
        setContainerItems([]);
      }
    } catch (error) {
      console.error("API error:", error);
    }
    finally {
      setLoading(false);
    }
  };  


  

  // batch code list for filter dropdown 
    const getContainerBatchCodes = async () => {
      try {
        const response = await ContainerInventoryService.getContainerBatchCodes();
  
        if (response.status && response.statusCode === 200) {
          const codes = response.data.map((item: any) => item.batch_no);
          setBatchCodes(codes);
        }
      } catch (error) {
        console.error("Batch code API error:", error);
      }
    };



  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);

    if (size) {
      setPageSize(size);
    }
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setStatusFilter('');
    setLotFilter('');
    setMediaTypeFilter('');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };

  const showToastMessage = (message: string, type: string) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  useEffect(() => {
    getContainerList();
  }, [currentPage, pageSize, sort, order, searchTerm,statusFilter, lotFilter, mediaTypeFilter, fromDate, toDate]);
  
  useEffect(() => {
    getContainerBatchCodes();
  }, []);

  useEffect(() => {
    getContainerInventoryStats();
  }, []);

  useEffect(() => {
    getContainerPlateTypeList();
  }, []);



  if (view === "lot" && selectedBatchId) {
    return (
      <ContainerDetails
        batchId={selectedBatchId}
        onBack={() => {
          setView("list");
          getContainerList();
          getContainerInventoryStats();
          getContainerBatchCodes();
        }}
      />
    );
  }



  return (
    <div className="media-prep-scope">
    <MediaPrepHeaderSection />
    <MediaPrepPageHeader pageHeader={t('containerInventory.title')} pageDescription={t('pageDescription.mediaContainerInventory.description')} />

    <div className="media-prep-scope px-8 pb-13 pt-3 max-w-full animate-in fade-in duration-500 min-h-full">
      <div className="flex items-start justify-between mb-2">
        <div className="space-y-1">
          {/* <h2 className='headText1 m-0 d-flex align-items-center' style={{ fontSize: '18px', height: '100%' }}>{t('containerInventory.title')}</h2>
          <p className="text-slate-400 text-[12.5px] font-medium">{t('containerInventory.description')}</p> */}
        </div>
        <div className="flex gap-4 ml-[185px] mt-1">
          {permission?.includes("reconcile_stock_container") && (
          <button 
            onClick={() => navigate("/reconcileContainer")}
            className="bg-white text-slate-700 border border-slate-200 flex items-center gap-2 px-6 py-1 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm focus:outline-none focus:ring-0 active:outline-none"
          >
            <span className="material-symbols-outlined text-xl">{t('mediaPowder.icons.rebase_edit')}</span> {t('containerInventory.actions.reconcileStock')}
          </button>
          )}
          {permission?.includes("add_container_inventory") && (
          <button 
            onClick={() => setShowLogModal(true)}
            className="bg-[#FF7043] text-white flex items-center gap-1 px-4 py-2 rounded-lg shadow-[0_8px_20px_rgba(255,112,67,0.2)] font-semibold text-xs uppercase tracking-wide hover:brightness-110 active:scale-95 transition-all border-none"
          >
            <span className="material-symbols-outlined">{t('mediaPowder.icons.add_box')}</span> {t('containerInventory.actions.logShipment')}
          </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-3.5">
        {[
            {label: t("containerInventory.statistics.available"),val: stats.available.toLocaleString(),color: '#10B981',icon: 'check_circle',trend: t("containerInventory.statistics.readyForPrep"),status: t("containerInventory.statistics.availableSterile"),},
            {label: t("containerInventory.statistics.inSterilizer"),val: stats.sterilization.toLocaleString(),color: '#3B82F6',icon: 'change_circle',trend: t("containerInventory.statistics.activeCycle"),status: t("containerInventory.statistics.inSterilizer"),},
            {label: t("containerInventory.statistics.inUse"),val: stats.inUse.toLocaleString(),color: '#F59E0B',icon: 'biotech',trend: t("containerInventory.statistics.distributed"),status: t("containerInventory.statistics.inUse"),},
            {label: t("containerInventory.statistics.blocked"),val: stats.blocked.toLocaleString(),color: '#EF4444',icon: 'warning',trend: t("containerInventory.statistics.quarantine"),status: t("containerInventory.statistics.blocked"),},
        ].map((box, i) => (
            <StatusCard
            key={i}
            label={box.label}
            value={box.val}
            color={box.color}
            icon={box.icon}
            trend={box.trend}
            status={box.status}
            selectedStatus={statusFilter}
            />
        ))}
        </div>

      <FilterBar
        batchCodes={batchCodes}
        mediaTypes={plateTypes}
        selectedBatchCode={lotFilter}
        selectedStatus={statusFilter}
        selectedMediaType={mediaTypeFilter}
        fromDate={fromDate}
        toDate={toDate}
        searchTerm={searchInput}

        onBatchCodeChange={(val) => {
          setLotFilter(val);
          setCurrentPage(1);
        }}

        onMediaTypeChange={(val) => {
          setMediaTypeFilter(val);
          setCurrentPage(1);
        }}

        onFromDateChange={(val) => {
          setFromDate(val);
          setCurrentPage(1);
        }}

        onToDateChange={(val) => {
          setToDate(val);
          setCurrentPage(1);
        }}

        onSearchChange={(val) => {
          setSearchInput(val);

          if (!val) {
            setSearchTerm("");
            setCurrentPage(1);
          }
        }}

        onSearchClick={() => {
          setSearchTerm(searchInput);
          setCurrentPage(1);
        }}

        onClearFilters={handleClearFilters}
      />

      <ContainerInventoryTable
        columns={getColumns(t)}
        data={containerItems}
        loading={loading}
        currentPage={currentPage}
        pageSize={pageSize}
        emptyMessage="No containers match your search"
        onSortClick={handleSort}
        renderAction={(row) => (
          <button
            onClick={() => {
              setSelectedBatchId(row.id);
              setView("lot");
            }}
            className="flex items-center justify-center gap-1 w-[90px] h-[26px] bg-[rgb(54,64,93)] text-white rounded-lg font-black text-[8px] uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all shadow-sm border-none outline-none focus:outline-none"
          >
            <span className="material-symbols-outlined text-base">{t('mediaPowder.icons.visibility')}</span>
              {t('mediaPowder.actions.view')}
          </button>
        )}
      />

      <CustomToast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={2500}
        message={toastMessage}
        toastType={toastType}
      />

      <LogMediaPowderModal
        show={showLogModal}
        onClose={() => setShowLogModal(false)}
        onSuccess={() => {
          setShowLogModal(false);
          getContainerList();
          getContainerInventoryStats();
          getContainerBatchCodes();
        }}
        title={t('containerInventory.addContainer.add')}
        FormComponent={ContainerLotCreation}
        formProps={{ showToastMessage }}
      />

      <Pagination
        page={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
      />
      </div>
     </div>
  );
};

export default ContainerInventory;
